import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetDiceRoundBetsQuery } from '../services/dice/diceServices';
import { useGetAviatorRoundBetsQuery } from '../services/aviator/aviatorApi';
import { useGetRouletteRoundBetsQuery } from '../services/roulette/rouletteApi';
import { useGetLudoInplayRoundsQuery, useGetLudoRoundBetsQuery } from '../services/ludo/ludoApi';
import { isLotus } from '../utils/domainConfig';

const LudoTable = ({ navigate }) => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, error } = useGetDiceRoundBetsQuery({ page, limit: 20 });

  const rows       = data?.data || [];
  const summary    = data?.summary || {};
  const totalPages = data?.pagination?.totalPages || 1;

  return (
    <>
      <div className="bg-gray-50 px-3 py-3 sm:px-5 sm:py-4 border-b border-gray-200">
        <div className="text-sm text-gray-600 mb-1">Overall P/L</div>
        <div className={`text-3xl font-semibold ${(summary.overallPL || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {Number(summary.overallPL || 0).toFixed(2)}
        </div>
      </div>

      <div className="overflow-x-auto">
        {isLoading || isFetching ? (
          <div className="flex justify-center py-8"><Loading /></div>
        ) : error ? (
          <div className="p-4 text-red-500 text-center">{error?.message || 'Error loading data'}</div>
        ) : (
          <table className="w-full border-collapse text-xs sm:text-sm">
            <thead>
              <tr className="bg-[#3e525f] text-white">
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">#</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">Date</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">Event Name</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">Total Bets</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">Total Stake</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">P/L</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-gray-400">No data found</td></tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={row.date || index} className="hover:bg-gray-50 border-t border-gray-200">
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-600">{(page - 1) * 20 + index + 1}</td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-700">{row.date || '-'}</td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 font-medium text-gray-800">{row.eventName || '-'}</td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-700">{row.totalBets ?? 0}</td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-700">{row.totalStake ?? 0}</td>
                    <td className={`py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 font-semibold ${(row.pl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Number(row.pl || 0).toFixed(2)}
                    </td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4">
                      <div className="flex gap-1.5 sm:gap-2">
                        <button
                          onClick={() => navigate(`/ludo/all-bets/${row.date}`)}
                          className={isLotus()
                            ? "bg-[#4361ee] text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm hover:bg-[#304ffe] transition-colors rounded-full"
                            : "bg-[#3e525f] text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm hover:bg-[#2d3f4a] transition-colors"}
                        >
                          Show Rounds
                        </button>
                        <button
                          onClick={() => navigate('/ludo/company-report', { state: { game: 'dice', gameName: 'Dice', fromDate: row.date, toDate: row.date } })}
                          className={isLotus()
                            ? "bg-[#3f9d85] hover:bg-[#358a74] text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm transition-colors rounded-full"
                            : "bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm transition-colors"}
                        >
                          PL
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-end gap-1 px-4 py-3 border-t border-gray-200">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-40">‹</button>
          <span className="px-3 py-1 bg-[#3e525f] text-white text-sm">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-40">›</button>
        </div>
      )}
    </>
  );
};

const AviatorTable = ({ navigate }) => {
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, error } = useGetAviatorRoundBetsQuery({ page, limit: 20 });

  const rows       = data?.data || [];
  const summary    = data?.summary || {};
  const totalPages = data?.pagination?.totalPages || 1;

  return (
    <>
      <div className="bg-gray-50 px-3 py-3 sm:px-5 sm:py-4 border-b border-gray-200">
        <div className="text-sm text-gray-600 mb-1">Overall P/L</div>
        <div className={`text-3xl font-semibold ${(summary.overallPL || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {Number(summary.overallPL || 0).toFixed(2)}
        </div>
      </div>

      <div className="overflow-x-auto">
        {isLoading || isFetching ? (
          <div className="flex justify-center py-8"><Loading /></div>
        ) : error ? (
          <div className="p-4 text-red-500 text-center">{error?.message || 'Error loading data'}</div>
        ) : (
          <table className="w-full border-collapse text-xs sm:text-sm">
            <thead>
              <tr className="bg-[#1a1c2e] text-white">
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">#</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">Date</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">Total Bets</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">Total Stake</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">P/L</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-gray-400">No data found</td></tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={row.roundId || index} className="hover:bg-gray-50 border-t border-gray-200">
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-600">{(page - 1) * 20 + index + 1}</td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-700">{row.date || row.settledAt?.slice(0, 10) || '-'}</td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-700">{row.totalBets ?? 0}</td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-700">{row.totalStake ?? 0}</td>
                    <td className={`py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 font-semibold ${(row.pl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Number(row.pl || 0).toFixed(2)}
                    </td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4">
                      <div className="flex gap-1.5 sm:gap-2">
                        <button
                          onClick={() => navigate(`/ludo/aviator-rounds/${row.date || row.settledAt?.slice(0, 10)}`)}
                          className={isLotus()
                            ? "bg-[#4361ee] hover:bg-[#304ffe] text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm transition-colors rounded-full"
                            : "bg-[#1a1c2e] hover:bg-[#2d3348] text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm transition-colors"}
                        >
                          Show Rounds
                        </button>
                        <button
                          onClick={() => navigate('/ludo/aviator-company-report', { state: { gameName: 'Aviator', fromDate: row.date || row.settledAt?.slice(0, 10), toDate: row.date || row.settledAt?.slice(0, 10) } })}
                          className={isLotus()
                            ? "bg-[#3f9d85] hover:bg-[#358a74] text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm transition-colors rounded-full"
                            : "bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm transition-colors"}
                        >
                          PL
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-end gap-1 px-4 py-3 border-t border-gray-200">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-40">‹</button>
          <span className="px-3 py-1 bg-[#1a1c2e] text-white text-sm">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-40">›</button>
        </div>
      )}
    </>
  );
};

const LudoGameTable = ({ navigate }) => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, error } = useGetLudoRoundBetsQuery({ page, limit: 20 });

  const rows       = data?.data || [];
  const summary    = data?.summary || {};
  const totalPages = data?.pagination?.totalPages || 1;

  return (
    <>
      <div className="bg-gray-50 px-3 py-3 sm:px-5 sm:py-4 border-b border-gray-200">
        <div className="text-sm text-gray-600 mb-1">Overall P/L</div>
        <div className={`text-3xl font-semibold ${(summary.overallPL || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {Number(summary.overallPL || 0).toFixed(2)}
        </div>
      </div>

      <div className="overflow-x-auto">
        {isLoading || isFetching ? (
          <div className="flex justify-center py-8"><Loading /></div>
        ) : error ? (
          <div className="p-4 text-red-500 text-center">{error?.message || 'Error loading data'}</div>
        ) : (
          <table className="w-full border-collapse text-xs sm:text-sm">
            <thead>
              <tr className="bg-[#3e525f] text-white">
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">#</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">Date</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">Total Stake</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-gray-600">P/L</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-gray-400">No data found</td></tr>
              ) : (
                rows.map((row, index) => {
                  const rowDate = row.date || (row.settledAt ? row.settledAt.slice(0, 10) : undefined);
                  return (
                    <tr key={rowDate || index} className="hover:bg-gray-50 border-t border-gray-200">
                      <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-600">{(page - 1) * 20 + index + 1}</td>
                      <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-700">{rowDate || '-'}</td>
                      <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-700">{row.totalStake ?? 0}</td>
                      <td className={`py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 font-semibold ${(row.pl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {Number(row.pl || 0).toFixed(2)}
                      </td>
                      <td className="py-2 px-2 sm:py-3 sm:px-4">
                        <div className="flex gap-1.5 sm:gap-2">
                          <button
                            onClick={() => navigate(`/ludo/game-rounds/${rowDate}`)}
                            className={isLotus()
                              ? "bg-[#4361ee] text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm hover:bg-[#304ffe] transition-colors rounded-full"
                              : "bg-[#3e525f] text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm hover:bg-[#2d3f4a] transition-colors"}
                          >
                            Show Rounds
                          </button>
                          <button
                            onClick={() => navigate('/ludo/company-report', { state: { game: 'ludo', gameName: 'Ludo', fromDate: rowDate, toDate: rowDate } })}
                            className={isLotus()
                              ? "bg-[#3f9d85] hover:bg-[#358a74] text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm transition-colors rounded-full"
                              : "bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm transition-colors"}
                          >
                            PL
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-end gap-1 px-4 py-3 border-t border-gray-200">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-40">‹</button>
          <span className="px-3 py-1 bg-[#3e525f] text-white text-sm">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-40">›</button>
        </div>
      )}
    </>
  );
};

const RouletteTable = ({ navigate }) => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, error } = useGetRouletteRoundBetsQuery({ page, limit: 20 });

  const rows       = data?.data || [];
  const summary    = data?.summary || {};
  const totalPages = data?.pagination?.totalPages || 1;
  const THEME      = isLotus() ? '#0a0a0a' : '#7c3aed';

  return (
    <>
      <div className="bg-gray-50 px-3 py-3 sm:px-5 sm:py-4 border-b border-gray-200">
        <div className="text-sm text-gray-600 mb-1">Overall P/L</div>
        <div className={`text-3xl font-semibold ${(summary.overallPL || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {Number(summary.overallPL || 0).toFixed(2)}
        </div>
      </div>

      <div className="overflow-x-auto">
        {isLoading || isFetching ? (
          <div className="flex justify-center py-8"><Loading /></div>
        ) : error ? (
          <div className="p-4 text-red-500 text-center">{error?.message || 'Error loading data'}</div>
        ) : (
          <table className="w-full border-collapse text-xs sm:text-sm">
            <thead>
              <tr className="text-white" style={{ background: THEME }}>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-purple-400">#</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-purple-400">Date</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-purple-400">Total Bets</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-purple-400">Total Stake</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium border-r border-purple-400">P/L</th>
                <th className="py-2 px-2 sm:py-3 sm:px-4 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-gray-400">No data found</td></tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={row.date || index} className="hover:bg-gray-50 border-t border-gray-200">
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-600">{(page - 1) * 20 + index + 1}</td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-700">{row.date || '-'}</td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-700">{row.totalBets ?? 0}</td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 text-gray-700">{row.totalStake ?? 0}</td>
                    <td className={`py-2 px-2 sm:py-3 sm:px-4 border-r border-gray-200 font-semibold ${(row.pl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Number(row.pl || 0).toFixed(2)}
                    </td>
                    <td className="py-2 px-2 sm:py-3 sm:px-4">
                      <div className="flex gap-1.5 sm:gap-2">
                        <button
                          onClick={() => navigate(`/roulette/rounds/${row.date}`)}
                          className={isLotus()
                            ? "bg-[#4361ee] hover:bg-[#304ffe] text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm transition-colors rounded-full"
                            : "text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm transition-colors hover:opacity-90"}
                          style={isLotus() ? undefined : { background: THEME }}
                        >
                          Show Rounds
                        </button>
                        <button
                          onClick={() => navigate(`/casino-ledger/roulette/${row.date}`)}
                          className={isLotus()
                            ? "bg-[#3f9d85] hover:bg-[#358a74] text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm transition-colors rounded-full"
                            : "bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 text-xs sm:px-4 sm:py-1.5 sm:text-sm transition-colors"}
                        >
                          PL
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex justify-end gap-1 px-4 py-3 border-t border-gray-200">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-40">‹</button>
          <span className="px-3 py-1 text-white text-sm" style={{ background: THEME }}>{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-40">›</button>
        </div>
      )}
    </>
  );
};

const CompletedLudoPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dice');

  // Same API used on the Inplay page (/ludo/inplay) — drives the tab names
  const { data: gamesData } = useGetLudoInplayRoundsQuery();
  const games = gamesData?.data || [];
  const tabs = games.map(g => ({ key: (g.name || '').toLowerCase(), label: g.name }));

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full max-w-6xl px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-[#3e525f] px-3 py-2.5 sm:px-5 sm:py-3 flex items-center justify-between">
              <span className="text-xl text-white font-normal">Completed Games</span>
              <button onClick={() => navigate(-1)} className="bg-white text-gray-700 px-4 py-1.5 hover:bg-gray-100 transition-colors">
                Back
              </button>
            </div>

            {/* Tabs */}
            <div className="px-3 py-2.5 sm:px-5 sm:py-3 bg-gray-50 border-b border-gray-200 flex gap-1.5 sm:gap-2 flex-wrap">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3 py-1.5 text-xs sm:px-5 sm:py-2 sm:text-sm rounded-full font-semibold transition-all duration-200 ${
                    activeTab === tab.key
                      ? 'bg-[#3e525f] text-white shadow-sm'
                      : 'bg-white text-gray-500 border border-gray-200 hover:border-[#3e525f] hover:text-[#3e525f]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'dice'     && <LudoTable     navigate={navigate} />}
            {activeTab === 'ludo'     && <LudoGameTable navigate={navigate} />}
            {activeTab === 'aviator'  && <AviatorTable  navigate={navigate} />}
            {activeTab === 'roulette' && <RouletteTable navigate={navigate} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletedLudoPage;
